// src/components/admin/Plans.tsx
import React, { useEffect, useMemo, useState } from "react";
import {
  DndContext,
  closestCenter,
  useSensor,
  useSensors,
  PointerSensor,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Link, useNavigate } from "react-router-dom";
import { http } from "../../lib/http";

/* =========================
 * 型
 * ========================= */
type AdminPlan = {
  id: number;
  visible: boolean;
  publish: boolean;
  name: string;
  point: number;
  date: string;
  description: string;
  salesCount: number;
  sort?: number;
  supplyCount?: number; // 0 or undefined = 無制限
  recommended?: boolean; // ホームの「本日の一押し」

  // 表示期間
  startAt?: string; // ISO文字列（サーバー側）
  endAt?: string; // ISO文字列（サーバー側）
};

/* =========================
 * ユーティリティ
 * ========================= */
const saleStatusOf = (p: AdminPlan) => {
  if (!p.publish) return "売切れ";
  if (typeof p.supplyCount === "number" && p.supplyCount > 0) {
    return (p.salesCount ?? 0) >= p.supplyCount ? "売切れ" : "販売中";
  }
  return "販売中";
};

function normalizePlans(payload: unknown): AdminPlan[] {
  const list = Array.isArray((payload as any)?.items)
    ? (payload as any).items
    : Array.isArray(payload)
    ? (payload as any)
    : [];
  return list.map((p: any) => ({
    ...p,
    id: Number(p.id),
    point: Number(p.point),
  }));
}

// ISO → 一覧用の短い表示文字列
function formatPeriod(startAt?: string, endAt?: string): string {
  if (!startAt && !endAt) return "";
  const fmt = (iso?: string) => {
    if (!iso) return "";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    const hh = String(d.getHours()).padStart(2, "0");
    const mi = String(d.getMinutes()).padStart(2, "0");
    return `${mm}/${dd} ${hh}:${mi}`;
  };

  const s = fmt(startAt);
  const e = fmt(endAt);

  if (s && e) return `${s} 〜 ${e}`;
  if (s) return `${s} 〜`;
  if (e) return `〜 ${e}`;
  return "";
}

/* =========================
 * 行コンポーネント
 * ========================= */
const Row = ({
  plan,
  checked,
  onToggleCheck,
  onToggleVisible,
  onTogglePublish,
  onToggleRecommended,
  onDuplicate,
  onDelete,
  onEdit,
}: {
  plan: AdminPlan;
  checked: boolean;
  onToggleCheck: (id: number) => void;
  onToggleVisible: (id: number) => void;
  onTogglePublish: (id: number) => void;
  onToggleRecommended: (p: AdminPlan) => void;
  onDuplicate: (base: AdminPlan) => void;
  onDelete: (id: number) => void;
  onEdit: (plan: AdminPlan) => void;
}) => {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({
      id: plan.id,
    });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const saleStatus = saleStatusOf(plan);
  const saleBg = saleStatus === "販売中" ? "#3ad45a" : "#aaa";

  const periodText = formatPeriod(plan.startAt, plan.endAt);

  return (
    <tr ref={setNodeRef} style={style}>
      {/* チェックボックス */}
      <td style={{ textAlign: "center" }}>
        <input
          type="checkbox"
          checked={checked}
          onChange={() => onToggleCheck(plan.id)}
        />
      </td>

      {/* 表示 */}
      <td style={{ textAlign: "center" }}>
        <button
          onClick={() => onToggleVisible(plan.id)}
          style={{
            background: plan.visible ? "#1976d2" : "#ccc",
            color: "#fff",
            border: "none",
            borderRadius: 4,
            padding: "4px 10px",
          }}
        >
          {plan.visible ? "表示" : "非表示"}
        </button>
      </td>

      {/* 販売状況 */}
      <td style={{ textAlign: "center" }}>
        <button
          onClick={() => onTogglePublish(plan.id)}
          style={{
            background: saleBg,
            color: "#fff",
            border: "none",
            borderRadius: 4,
            padding: "4px 10px",
          }}
          title="クリックで切替"
        >
          {saleStatus}
        </button>
      </td>

      {/* 並べ替えハンドル：プラン名セル（幅広げ） */}
      <td
        {...attributes}
        {...listeners}
        style={{
          cursor: "grab",
          maxWidth: 320,
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {plan.name}
      </td>

      <td>{plan.point}</td>
      <td>{plan.date}</td>

      {/* 販売期間（幅広げ） */}
      <td style={{ maxWidth: 320, whiteSpace: "nowrap" }}>
        {periodText && (
          <div
            style={{
              fontSize: 12,
              lineHeight: 1.4,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {periodText}
          </div>
        )}
      </td>

      {/* オススメ */}
      <td style={{ textAlign: "center" }}>
        <button
          onClick={() => onToggleRecommended(plan)}
          style={{
            background: plan.recommended ? "#f6b23e" : "#ccc",
            color: "#fff",
            border: "none",
            borderRadius: 4,
            padding: "4px 10px",
            minWidth: 56,
          }}
          title="ホームの本日の一押しに表示"
        >
          オススメ
        </button>
      </td>

      {/* 操作 */}
      <td style={{ textAlign: "center" }}>
        <button
          onClick={() => onDuplicate(plan)}
          style={{
            marginRight: 8,
            padding: "4px 10px",
            border: "1px solid #888",
            borderRadius: 4,
          }}
        >
          複製
        </button>
        <button
          onClick={() => onEdit(plan)}
          style={{
            marginRight: 8,
            padding: "4px 10px",
            background: "#ffa726",
            color: "#fff",
            border: "none",
            borderRadius: 4,
          }}
        >
          編集
        </button>
        <button
          onClick={() => onDelete(plan.id)}
          style={{
            padding: "4px 10px",
            background: "#e53935",
            color: "#fff",
            border: "none",
            borderRadius: 4,
          }}
        >
          削除
        </button>
      </td>
    </tr>
  );
};

/* =========================
 * 画面本体
 * ========================= */
const PAGE_SIZE = 50;

const Plans = () => {
  const [plans, setPlans] = useState<AdminPlan[]>([]);
  const [page, setPage] = useState(1);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const navigate = useNavigate();
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } })
  );

  // ★ 一括日程用の入力値
  const [bulkDate, setBulkDate] = useState("");
  const [bulkPublishTime, setBulkPublishTime] = useState("");
  const [bulkStartAt, setBulkStartAt] = useState("");
  const [bulkEndAt, setBulkEndAt] = useState("");

  const reload = async () => {
    try {
      const payload = await http.get<AdminPlan[] | { items: AdminPlan[] }>(
        "/admin/plans"
      );
      const list = normalizePlans(payload);
      list.sort(
        (a, b) =>
          (a.sort ?? 9999) - (b.sort ?? 9999) ||
          Number(a.id) - Number(b.id)
      );
      setPlans(list);
      setPage(1); // 読み直したら先頭ページへ
      setSelectedIds([]);
    } catch (err) {
      console.error("load admin plans failed:", err);
      setPlans([]);
      setSelectedIds([]);
    }
  };

  useEffect(() => {
    reload();
  }, []);

  // ページング
  const totalPages = Math.max(1, Math.ceil(plans.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);

  const pagedPlans = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    const end = start + PAGE_SIZE;
    return plans.slice(start, end);
  }, [plans, currentPage]);

  // 並べ替え保存（失敗検知＋再読込）
  const saveOrder = async (ids: number[]) => {
    try {
      await http.post("/admin/plans/reorder", { ids: ids.map(Number) });
    } catch (err) {
      console.error("saveOrder failed:", err);
      alert("並び替えの保存に失敗しました。サーバーログを確認してください。");
    } finally {
      // サーバー側が成功していれば正順に、失敗なら元に戻す
      await reload();
    }
  };

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return;

    let newIds: number[] = [];
    setPlans((prev) => {
      const oldIndex = prev.findIndex((p) => p.id === Number(active.id));
      const newIndex = prev.findIndex((p) => p.id === Number(over.id));
      const next = arrayMove(prev, oldIndex, newIndex);
      newIds = next.map((p) => p.id);
      return next;
    });

    if (newIds.length) {
      saveOrder(newIds);
    }
  };

  // チェック関連
  const toggleCheck = (id: number) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const pageIds = pagedPlans.map((p) => p.id);
  const allCheckedOnPage =
    pageIds.length > 0 && pageIds.every((id) => selectedIds.includes(id));

  const toggleCheckAllOnPage = () => {
    setSelectedIds((prev) => {
      if (allCheckedOnPage) {
        // このページ分だけ外す
        return prev.filter((id) => !pageIds.includes(id));
      }
      // このページ分を追加
      const set = new Set(prev);
      pageIds.forEach((id) => set.add(id));
      return Array.from(set);
    });
  };

  // トグル/CRUD
  const onToggleVisible = async (id: number) => {
    // 楽観更新（即時反映）
    setPlans((prev) => {
      const i = prev.findIndex((p) => p.id === id);
      if (i === -1) return prev;
      const next = [...prev];
      next[i] = { ...next[i], visible: !next[i].visible };
      return next;
    });
    try {
      await http.patch(`/admin/plans/${id}/visible`, {});
    } catch (e) {
      // 失敗時はロールバック
      setPlans((prev) => {
        const i = prev.findIndex((p) => p.id === id);
        if (i === -1) return prev;
        const next = [...prev];
        next[i] = { ...next[i], visible: !next[i].visible };
        return next;
      });
      alert("表示の切替に失敗しました。サーバーログを確認してください。");
    }
  };

  const onTogglePublish = async (id: number) => {
    // 楽観更新（即時反映）
    setPlans((prev) => {
      const i = prev.findIndex((p) => p.id === id);
      if (i === -1) return prev;
      const next = [...prev];
      next[i] = { ...next[i], publish: !next[i].publish };
      return next;
    });
    try {
      await http.patch(`/admin/plans/${id}/publish`, {});
    } catch (e) {
      // 失敗時はロールバック
      setPlans((prev) => {
        const i = prev.findIndex((p) => p.id === id);
        if (i === -1) return prev;
        const next = [...prev];
        next[i] = { ...next[i], publish: !next[i].publish };
        return next;
      });
      alert(
        "販売状況の切替に失敗しました。サーバーログを確認してください。"
      );
    }
  };

  const onToggleRecommended = async (p: AdminPlan) => {
    // 楽観更新：即反映
    setPlans((prev) => {
      const i = prev.findIndex((x) => x.id === p.id);
      if (i === -1) return prev;
      const next = [...prev];
      next[i] = { ...next[i], recommended: !next[i].recommended };
      return next;
    });

    try {
      await http.put(`/admin/plans/${p.id}`, {
        recommended: !p.recommended,
      });
    } catch (e) {
      // 失敗時はロールバック
      setPlans((prev) => {
        const i = prev.findIndex((x) => x.id === p.id);
        if (i === -1) return prev;
        const next = [...prev];
        next[i] = { ...next[i], recommended: !next[i].recommended };
        return next;
      });
      alert("オススメ切替に失敗しました。サーバーログを確認してください。");
    }
  };

  // 単体複製：サーバ側で sourceId を受けて完全コピー（画像URL含む）
  const onDuplicate = async (base: AdminPlan) => {
    const rec = await http.post<AdminPlan>(`/admin/plans`, {
      sourceId: base.id,
      name: `${base.name}（複製）`,
    });

    setPlans((prev) => {
      const next = [...prev, rec].sort(
        (a, b) =>
          (a.sort ?? 9999) - (b.sort ?? 9999) ||
          Number(a.id) - Number(b.id)
      );
      return next;
    });
  };

  const onDelete = async (id: number) => {
    if (!window.confirm("削除しますか？")) return;
    await http.del(`/admin/plans/${id}`);
    reload();
  };

  const onEdit = (plan: AdminPlan) => {
    navigate(`/admin/plans/${plan.id}/edit`, { state: { plan } });
  };

  // 一括複製（選択分をループで /admin/plans POST）
  const handleBulkDuplicate = async () => {
    if (!selectedIds.length) {
      alert("複製するプランを選択してください。");
      return;
    }
    if (
      !window.confirm(
        `選択中の ${selectedIds.length} 件のプランを複製します。よろしいですか？`
      )
    ) {
      return;
    }

    try {
      for (const id of selectedIds) {
        const base = plans.find((p) => p.id === id);
        if (!base) continue;
        await http.post(`/admin/plans`, {
          sourceId: base.id,
          name: `${base.name}（複製）`,
        });
      }
      await reload();
      alert("一括複製が完了しました。");
    } catch (e) {
      console.error("bulk duplicate failed:", e);
      alert("一括複製に失敗しました。サーバーログを確認してください。");
    }
  };

  // 一括削除（選択分をループで /admin/plans/:id DELETE）
  const handleBulkDelete = async () => {
    if (!selectedIds.length) {
      alert("削除するプランを選択してください。");
      return;
    }
    if (
      !window.confirm(
        `選択中の ${selectedIds.length} 件のプランを削除します。元に戻せません。よろしいですか？`
      )
    ) {
      return;
    }

    try {
      for (const id of selectedIds) {
        await http.del(`/admin/plans/${id}`);
      }
      await reload();
      alert("一括削除が完了しました。");
    } catch (e) {
      console.error("bulk delete failed:", e);
      alert("一括削除に失敗しました。サーバーログを確認してください。");
    }
  };

  // ★ 一括「（複製）」削除
  const handleBulkClearCopyMark = async () => {
    if (!selectedIds.length) {
      alert("名前から「（複製）」を削除するプランを選択してください。");
      return;
    }

    if (
      !window.confirm(
        `選択中の ${selectedIds.length} 件のプラン名から「（複製）」を一括削除します。よろしいですか？`
      )
    ) {
      return;
    }

    try {
      for (const id of selectedIds) {
        const p = plans.find((x) => x.id === id);
        if (!p) continue;
        const newName = p.name
          .replace(/（複製）/g, "")
          .replace(/\(複製\)/g, "")
          .trim();
        if (newName === p.name) continue;
        await http.put(`/admin/plans/${id}`, { name: newName });
      }
      await reload();
      alert("「（複製）」の一括削除が完了しました。");
    } catch (e) {
      console.error("bulk clear copy mark failed:", e);
      alert(
        "「（複製）」の一括削除に失敗しました。サーバーログを確認してください。"
      );
    }
  };

  // ★ 一括日程変更（開催日・情報公開時間・販売開始/終了）
  const handleBulkUpdateSchedule = async () => {
    if (!selectedIds.length) {
      alert("日程を変更するプランを選択してください。");
      return;
    }

    const payload: any = {};
    if (bulkDate) payload.date = bulkDate;
    if (bulkPublishTime) payload.publishTime = bulkPublishTime;
    if (bulkStartAt) payload.startAt = bulkStartAt;
    if (bulkEndAt) payload.endAt = bulkEndAt;

    if (!Object.keys(payload).length) {
      alert(
        "変更する項目（開催日・情報公開時間・販売開始/終了時間）を入力してください。"
      );
      return;
    }

    if (
      !window.confirm(
        `選択中の ${selectedIds.length} 件のプランに、入力された日程を一括で反映します。よろしいですか？`
      )
    ) {
      return;
    }

    try {
      for (const id of selectedIds) {
        await http.put(`/admin/plans/${id}`, payload);
      }
      await reload();
      alert("日程の一括更新が完了しました。");
    } catch (e) {
      console.error("bulk update schedule failed:", e);
      alert("日程の一括更新に失敗しました。サーバーログを確認してください。");
    }
  };

  const items = useMemo(() => pagedPlans.map((p) => p.id), [pagedPlans]);

  return (
    <div style={{ maxWidth: 1300, margin: "40px auto" }}>
      <div style={{ display: "flex", gap: 12, marginBottom: 16 }}>
        <Link
          to="/admin/plans/new"
          style={{
            padding: "8px 16px",
            background: "#1976d2",
            color: "#fff",
            borderRadius: 4,
            textDecoration: "none",
          }}
        >
          プラン作成
        </Link>
        <button
          style={{
            padding: "8px 16px",
            background: "#666",
            color: "#fff",
            border: "none",
            borderRadius: 4,
          }}
          onClick={() => navigate("/admin/sales")}
        >
          購入履歴
        </button>
        <button
          style={{
            padding: "8px 16px",
            background: "#aaa",
            color: "#222",
            border: "none",
            borderRadius: 4,
          }}
          onClick={reload}
        >
          再読込
        </button>
        {/* ★ 追加：再読込の横にプラン購入履歴ボタン */}
        <button
          style={{
            padding: "8px 16px",
            background: "#4caf50",
            color: "#fff",
            border: "none",
            borderRadius: 4,
          }}
          onClick={() => navigate("/admin/sales")}
        >
          プラン購入履歴
        </button>

        {/* 一括操作ボタン */}
        <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
          <button
            type="button"
            onClick={handleBulkDuplicate}
            style={{
              padding: "8px 12px",
              borderRadius: 4,
              border: "1px solid #555",
              background: "#fff",
              cursor: "pointer",
            }}
          >
            一括複製
          </button>
          <button
            type="button"
            onClick={handleBulkClearCopyMark}
            style={{
              padding: "8px 12px",
              borderRadius: 4,
              border: "1px solid #555",
              background: "#fff",
              cursor: "pointer",
            }}
          >
            「（複製）」一括削除
          </button>
          <button
            type="button"
            onClick={handleBulkDelete}
            style={{
              padding: "8px 12px",
              borderRadius: 4,
              border: "1px solid #e53935",
              background: "#fff",
              color: "#e53935",
              cursor: "pointer",
            }}
          >
            一括削除
          </button>
        </div>
      </div>

      {/* ★ 一括日程変更フォーム */}
      <div
        style={{
          marginBottom: 16,
          padding: 12,
          borderRadius: 4,
          border: "1px solid #ddd",
          background: "#fafafa",
          fontSize: 13,
        }}
      >
        <div style={{ marginBottom: 8, fontWeight: 600 }}>
          選択したプランの「開催日・情報公開時間・販売開始／終了時間」を一括変更
        </div>
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: 8,
            alignItems: "center",
          }}
        >
          <label>
            開催日：
            <input
              type="date"
              value={bulkDate}
              onChange={(e) => setBulkDate(e.target.value)}
              style={{ marginLeft: 4 }}
            />
          </label>

          <label>
            情報公開時間：
            <input
              type="datetime-local"
              value={bulkPublishTime}
              onChange={(e) => setBulkPublishTime(e.target.value)}
              style={{ marginLeft: 4 }}
            />
          </label>

          <label>
            販売開始：
            <input
              type="datetime-local"
              value={bulkStartAt}
              onChange={(e) => setBulkStartAt(e.target.value)}
              style={{ marginLeft: 4 }}
            />
          </label>

          <label>
            販売終了：
            <input
              type="datetime-local"
              value={bulkEndAt}
              onChange={(e) => setBulkEndAt(e.target.value)}
              style={{ marginLeft: 4 }}
            />
          </label>

          <button
            type="button"
            onClick={handleBulkUpdateSchedule}
            style={{
              padding: "6px 12px",
              borderRadius: 4,
              border: "none",
              background: "#1976d2",
              color: "#fff",
              cursor: "pointer",
              fontWeight: 600,
              marginLeft: "auto",
            }}
          >
            一括日程を反映
          </button>
        </div>
        <div style={{ marginTop: 4, color: "#666" }}>
          ※ 空欄の項目は変更されません。値を入れた項目だけが、選択中のプランに一括で適用されます。
        </div>
      </div>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext items={items} strategy={verticalListSortingStrategy}>
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              background: "#fff",
            }}
          >
            <thead>
              <tr style={{ background: "#e6e9ee", fontWeight: "bold" }}>
                <th style={{ padding: 10, width: 40 }}>
                  {/* ページ内全選択 */}
                  <input
                    type="checkbox"
                    checked={allCheckedOnPage}
                    onChange={toggleCheckAllOnPage}
                  />
                </th>
                <th style={{ padding: 10, width: 70 }}>表示</th>
                <th style={{ padding: 10, width: 90 }}>販売状況</th>
                {/* プラン名を広めに確保 */}
                <th style={{ padding: 10, width: 300 }}>プラン名</th>
                <th style={{ padding: 10, width: 100 }}>販売ポイント</th>
                <th style={{ padding: 10, width: 120 }}>開催日</th>
                {/* 販売期間も広めに確保 */}
                <th style={{ padding: 10, width: 320 }}>販売期間</th>
                <th style={{ padding: 10, width: 90 }}>オススメ</th>
                <th style={{ padding: 10, width: 200 }}>操作</th>
              </tr>
            </thead>

            <tbody>
              {pagedPlans.map((plan) => (
                <Row
                  key={plan.id}
                  plan={plan}
                  checked={selectedIds.includes(plan.id)}
                  onToggleCheck={toggleCheck}
                  onToggleVisible={onToggleVisible}
                  onTogglePublish={onTogglePublish}
                  onToggleRecommended={onToggleRecommended}
                  onDuplicate={onDuplicate}
                  onDelete={onDelete}
                  onEdit={onEdit}
                />
              ))}
            </tbody>
          </table>
        </SortableContext>
      </DndContext>

      {/* ▼ ページネーション */}
      <div
        style={{
          marginTop: 16,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          gap: 8,
          fontSize: 14,
        }}
      >
        <button
          type="button"
          onClick={() => setPage((p) => Math.max(1, p - 1))}
          disabled={currentPage <= 1}
          style={{
            padding: "4px 10px",
            borderRadius: 4,
            border: "1px solid #ccc",
            background: currentPage <= 1 ? "#eee" : "#fff",
            cursor: currentPage <= 1 ? "default" : "pointer",
          }}
        >
          前へ
        </button>
        <span>
          {currentPage} / {totalPages} ページ（全 {plans.length} 件）
        </span>
        <button
          type="button"
          onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
          disabled={currentPage >= totalPages}
          style={{
            padding: "4px 10px",
            borderRadius: 4,
            border: "1px solid #ccc",
            background: currentPage >= totalPages ? "#eee" : "#fff",
            cursor: currentPage >= totalPages ? "default" : "pointer",
          }}
        >
          次へ
        </button>
      </div>
    </div>
  );
};

export default Plans;
